<template>
  <div class="listing-views-container">
    <MainNavbar />
    
    <div class="views-content">
      <router-link to="/ipa/thirdparty/seller-dashboard" class="back-to-dashboard">
        <i class="fa-solid fa-arrow-left"></i>
        Back to Dashboard
      </router-link>
      <!-- HEADER -->
      <div class="views-header">
        <div class="header-content">
          <h1><i class="fa-solid fa-chart-line"></i> Listing Analytics</h1>
          <p class="subtitle">See how your listings are doing</p>
        </div>
      </div>

      <!-- OVERVIEW STATS -->
      <div class="overview-stats">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fa-solid fa-eye"></i>
          </div>
          <div class="stat-content">
            <h3>{{ formatNumber(totalViews) }}</h3>
            <p>Total Views</p>
            <div class="stat-change" :class="getChangeClass(viewsChange)">
              <i :class="getTrendIcon(viewsChange)"></i>
              {{ Math.abs(viewsChange) }}% (estimate) vs older listings
            </div>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fa-solid fa-store"></i>
          </div>
          <div class="stat-content">
            <h3>{{ activeListings }}</h3>
            <p>Active Listings</p>
            <div class="stat-change neutral">
              <i class="fa-solid fa-minus"></i>
              {{ newListings }} new in last 7 days
            </div>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fa-solid fa-fire"></i>
          </div>
          <div class="stat-content">
            <h3>{{ mostViewedListing?.title || 'N/A' }}</h3>
            <p>Most Viewed Listing</p>
            <div class="stat-change positive">
              <i class="fa-solid fa-arrow-up"></i>
              {{ mostViewedListing?.views || 0 }} views
            </div>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fa-solid fa-chart-simple"></i>
          </div>
          <div class="stat-content">
            <h3>{{ avgViewsPerListing.toFixed(1) }}</h3>
            <p>Avg Views per Listing</p>
            <div class="stat-change" :class="getChangeClass(avgViewsChange)">
              <i :class="getTrendIcon(avgViewsChange)"></i>
              {{ Math.abs(avgViewsChange) }}% (estimate)
            </div>
          </div>
        </div>
      </div>

      <!-- MAIN CONTENT GRID -->
      <div class="content-grid">
        <!-- VIEWS CHART -->
        <div class="chart-container">
          <div class="chart-header">
            <h3><i class="fa-solid fa-chart-column"></i> Views by Top Listings</h3>
          </div>
          <div class="chart-wrapper">
            <canvas ref="viewsChart"></canvas>
          </div>
        </div>

        <!-- TOP PERFORMERS -->
        <div class="top-performers">
          <div class="section-header">
            <h3><i class="fa-solid fa-trophy"></i> Top Performing Listings</h3>
            <button class="btn-refresh-small" @click="fetchListings">
              <i class="fa-solid fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            </button>
          </div>
          <div class="performers-list">
            <div 
              v-for="(listing, index) in topPerformers" 
              :key="listing.listing_id"
              class="performer-item"
              @click="viewListingDetails(listing)"
            >
              <div class="performer-rank">
                <span class="rank-badge" :class="getRankClass(index)">#{{ index + 1 }}</span>
                <div class="listing-image" v-if="listing.images && listing.images.length > 0">
                  <img
                    :src="listing.images[listing.coverIndex || 0]"
                    :alt="listing.title"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div class="performer-details">
                <h4 class="listing-title">{{ truncateText(listing.title, 40) }}</h4>
                <div class="listing-stats">
                  <div class="stat">
                    <i class="fa-solid fa-eye"></i>
                    <span>{{ formatNumber(listing.views) }} views</span>
                  </div>
                  <div class="stat">
                    <i class="fa-solid fa-money-bill-wave"></i>
                    <span>KES {{ listing.price.toLocaleString() }}</span>
                  </div>
                  <div class="stat">
                    <i class="fa-solid fa-calendar"></i>
                    <span>{{ formatDate(listing.created_at) }}</span>
                  </div>
                </div>
                <div class="view-progress">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :style="{ width: calculateViewPercentage(listing.views) + '%' }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ calculateViewPercentage(listing.views).toFixed(1) }}% of total</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- LISTINGS TABLE -->
        <div class="listings-table-container">
          <div class="table-header">
            <h3><i class="fa-solid fa-list"></i> All Listings</h3>
            <div class="table-controls">
              <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                  type="text" 
                  v-model="searchQuery" 
                  placeholder="Search listings..."
                  @input="handleSearch"
                />
              </div>
              <select v-model="sortBy" @change="sortListings" class="filter-select">
                <option value="views_desc">Most Views</option>
                <option value="views_asc">Least Views</option>
                <option value="price_desc">Highest Price</option>
                <option value="price_asc">Lowest Price</option>
                <option value="date_desc">Newest</option>
                <option value="date_asc">Oldest</option>
              </select>
            </div>
          </div>
          
          <div class="table-responsive">
            <table class="listings-table">
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Views</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="listing in paginatedListings" 
                  :key="listing.listing_id"
                  class="listing-row"
                  @click="viewListingDetails(listing)"
                >
                  <td class="listing-info">
                    <div class="listing-image-small">
                      <img
                        v-if="listing.images && listing.images.length > 0"
                        :src="listing.images[listing.coverIndex || 0]"
                        :alt="listing.title"
                        loading="lazy"
                        decoding="async"
                      />
                      <div v-else class="image-placeholder">
                        <i class="fa-solid fa-image"></i>
                      </div>
                    </div>
                    <div class="listing-text">
                      <strong>{{ truncateText(listing.title, 50) }}</strong>
                      <small>{{ listing.brand || 'No brand' }}</small>
                    </div>
                  </td>
                  <td class="category-cell">
                    <span class="category-badge">{{ listing.category }}</span>
                  </td>
                  <td class="price-cell">
                    <strong>KES {{ listing.price.toLocaleString() }}</strong>
                    <small v-if="listing.negotiable" class="negotiable-badge">Negotiable</small>
                  </td>
                  <td class="views-cell">
                    <div class="views-display">
                      <i class="fa-solid fa-eye"></i>
                      <span>{{ formatNumber(listing.views) }}</span>
                    </div>
                  </td>
                  <td class="status-cell">
                    <span :class="`status-badge status-${listing.is_sold ? 'sold' : listing.is_active ? 'active' : 'inactive'}`">
                      <i :class="getStatusIcon(listing)"></i>
                      {{ listing.is_sold ? 'Sold' : listing.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="date-cell">
                    {{ formatDate(listing.created_at) }}
                  </td>
                  <td class="actions-cell">
                    <div class="action-buttons">
                      <button 
                        class="btn-action btn-view"
                        @click.stop="viewListingDetails(listing)"
                        title="View Details"
                      >
                        <i class="fa-solid fa-chart-simple"></i>
                      </button>
                      <button 
                        class="btn-action btn-share"
                        @click.stop="shareListing(listing)"
                        title="Share Listing"
                      >
                        <i class="fa-solid fa-share"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- PAGINATION -->
          <div v-if="filteredListings.length > 0" class="pagination-controls">
            <div class="pagination-info">
              Showing {{ startItem }}-{{ endItem }} of {{ filteredListings.length }} listings
            </div>
            <div class="pagination-buttons">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="pagination-btn"
              >
                <i class="fa-solid fa-chevron-left"></i> Previous
              </button>
              
              <div class="page-numbers">
                <button 
                  v-for="page in totalPages" 
                  :key="page"
                  @click="goToPage(page)"
                  :class="{ active: page === currentPage }"
                  class="page-btn"
                >
                  {{ page }}
                </button>
              </div>
              
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages"
                class="pagination-btn"
              >
                Next <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
            
            <div class="page-size-selector">
              <label>Show:</label>
              <select v-model="pageSize" @change="resetPagination" class="page-size-select">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>

        <!-- QUICK INSIGHTS -->
        <div class="insights-container">
          <div class="section-header">
            <h3><i class="fa-solid fa-lightbulb"></i> Quick Insights</h3>
          </div>
          <div class="insights-list">
            <div class="insight-card" :class="getInsightClass(insight)" v-for="insight in insights" :key="insight.id">
              <div class="insight-icon">
                <i :class="insight.icon"></i>
              </div>
              <div class="insight-content">
                <h4>{{ insight.title }}</h4>
                <p>{{ insight.description }}</p>
                <div v-if="insight.action" class="insight-action">
                  <button @click="handleInsightAction(insight)" class="btn-insight">
                    {{ insight.action }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- DETAILS MODAL -->
      <div v-if="showDetailsModal" class="modal-overlay" @click.self="closeDetailsModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3><i class="fa-solid fa-chart-bar"></i> Listing Analytics</h3>
            <button class="modal-close" @click="closeDetailsModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          
          <div class="modal-body" v-if="selectedListing">
            <div class="listing-details">
              <div class="details-header">
                <div class="listing-title-section">
                  <h4>{{ selectedListing.title }}</h4>
                  <div class="listing-meta">
                    <span class="category">{{ selectedListing.category }}</span>
                    <span class="price">KES {{ selectedListing.price.toLocaleString() }}</span>
                    <span class="views"><i class="fa-solid fa-eye"></i> {{ selectedListing.views }} views</span>
                  </div>
                </div>
              </div>
              
              <div class="details-grid">
                <div class="detail-card">
                  <h5><i class="fa-solid fa-chart-line"></i> Performance Metrics</h5>
                  <div class="metrics-grid">
                    <div class="metric">
                      <span class="metric-label">Avg views per day</span>
                      <span class="metric-value">{{ calculateDailyViews(selectedListing) }}</span>
                    </div>
                    <div class="metric">
                      <span class="metric-label">Engagement score (estimate)</span>
                      <span class="metric-value">{{ calculateEngagementScore(selectedListing) }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Charts removed (single pie chart only) -->
              </div>
              
              <div class="detail-card full-width">
                <h5><i class="fa-solid fa-bullseye"></i> Optimization Suggestions</h5>
                <div class="suggestions">
                  <div v-for="suggestion in getOptimizationSuggestions(selectedListing)" 
                       :key="suggestion.id"
                       class="suggestion-item">
                    <i :class="suggestion.icon"></i>
                    <span>{{ suggestion.text }}</span>
                    <button class="btn-implement" @click="implementSuggestion(suggestion)">
                      Implement
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
/**
 * ListingViews module
 *
 * Purpose:
 * - Fetch seller listings and their view counts from the backend.
 * - Present analytics widgets (totals, trends, top performers).
 * - Render a pie chart for "Views by Top Listings".
 * - Provide table/search/sort/pagination and share-link actions.
 *
 * Data flow:
 * 1) `fetchListings()` loads data from `seller/listings/all`.
 * 2) Listing records are normalized (`views`, `created_at`) for UI safety.
 * 3) Derived metrics are computed via Vue computed properties.
 * 4) `initializeChart()` rebuilds the chart from current listing state.
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import Chart from 'chart.js/auto';
import Footer from "./Footer.vue";
import { buildApiUrl } from "@/config/api";

export default {
  name: "ListingViews",
  components: { MainNavbar, Footer },
  
  data() {
    return {
      loading: false,
      listings: [],
      filteredListings: [],
      searchQuery: "",
      sortBy: "views_desc",
      
      // Pagination
      currentPage: 1,
      pageSize: 10,
      
      // Modals
      showDetailsModal: false,
      selectedListing: null,
      
      // Charts
      viewsChart: null,
      
      // Insights
      insights: [],
      accountReady: false
    };
  },
  
  computed: {
    // Overview stats
    totalViews() {
      return this.listings.reduce((sum, listing) => sum + (listing.views || 0), 0);
    },
    
    activeListings() {
      return this.listings.filter(l => l.is_active && !l.is_sold).length;
    },
    
    mostViewedListing() {
      if (this.listings.length === 0) return null;
      return [...this.listings].sort((a, b) => (b.views || 0) - (a.views || 0))[0];
    },
    
    avgViewsPerListing() {
      if (this.listings.length === 0) return 0;
      return this.totalViews / this.listings.length;
    },
    
    newListings() {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return this.listings.filter(l => new Date(l.created_at) > oneWeekAgo).length;
    },
    
    // View change (estimate: newer listings vs older listings)
    viewsChange() {
      // We compare the newer half of listings with the older half.
      if (this.listings.length === 0) return 0;
      
      const sortedByDate = [...this.listings].sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      
      if (sortedByDate.length < 2) return 0;
      
      const midpoint = Math.floor(sortedByDate.length / 2);
      const olderHalf = sortedByDate.slice(0, midpoint);
      const newerHalf = sortedByDate.slice(midpoint);
      
      const olderAvg = olderHalf.reduce((sum, l) => sum + (l.views || 0), 0) / olderHalf.length || 0;
      const newerAvg = newerHalf.reduce((sum, l) => sum + (l.views || 0), 0) / newerHalf.length || 0;
      
      if (olderAvg === 0) return 0;
      return ((newerAvg - olderAvg) / olderAvg * 100).toFixed(1);
    },
    
    avgViewsChange() {
      // Uses the same estimate as viewsChange
      return this.viewsChange;
    },
    
    // Top performers
    topPerformers() {
      return [...this.listings]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 5);
    },
    
    // Pagination
    totalPages() {
      return Math.ceil(this.filteredListings.length / this.pageSize);
    },
    
    startItem() {
      return ((this.currentPage - 1) * this.pageSize) + 1;
    },
    
    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.filteredListings.length ? this.filteredListings.length : end;
    },
    
    paginatedListings() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredListings.slice(start, end);
    }
  },

  async beforeMount() {
    await this.ensureSellerAccountAccess();
  },
  
  async mounted() {
    await this.fetchListings();
    await this.generateInsights();
  },
  
  methods: {
    // API Methods
    async ensureSellerAccountAccess() {
      const token = localStorage.getItem("access_token") || localStorage.getItem("seller_token");
      if (!token) {
        this.$router.push("/ipa/sign-in");
        return false;
      }
      try {
        const res = await fetch(buildApiUrl("seller/me"), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) return true;
        if (res.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("seller_token");
          this.$router.push("/ipa/sign-in");
          return false;
        }

        let detail = "";
        try {
          const err = await res.json();
          detail = String(err?.detail || "").toLowerCase();
        } catch (e) {
          detail = "";
        }
        if (detail.includes("suspended")) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("seller_token");
          this.$router.push("/ipa/sign-in");
          return false;
        }
        return true;
      } catch {
        return true;
      }
    },

    // Returns auth headers used by protected seller endpoints.
    async getAuthHeaders() {
      const token = localStorage.getItem("access_token") || localStorage.getItem("seller_token");
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
    },
    
    // Fetches seller listings, normalizes required fields, then refreshes UI/chart state.
    async fetchListings() {
      this.loading = true;
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch(
          buildApiUrl('seller/listings/all'),
          {
            method: 'GET',
            headers: headers
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          this.listings = data.map(listing => ({
            ...listing,
            views: listing.views || 0,
            created_at: listing.created_at || new Date().toISOString()
          }));
          this.filteredListings = [...this.listings];
          this.sortListings();
          
          // Initialize chart with real data
          this.initializeChart();
          
          this.showToast('success', 'Success', `Loaded ${this.listings.length} listings`);
        } else {
          throw new Error('Failed to fetch listings');
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
        this.showToast('error', 'Error', 'Failed to load listings');
        // Don't create mock data - just show empty state
        this.listings = [];
        this.filteredListings = [];
      } finally {
        this.loading = false;
      }
    },
    
    
    async generateInsights() {
      // Generate insights based on real data
      const insights = [];
      
      if (this.listings.length === 0) {
        this.insights = [];
        return;
      }
      
      // Insight 1: Overall trend
      if (this.viewsChange > 0) {
        insights.push({
          id: 1,
          icon: "fa-solid fa-arrow-trend-up",
          title: `Views are up ${Math.abs(this.viewsChange)}%`,
          description: "Newer listings have more views than older ones",
          type: "positive"
        });
      } else if (this.viewsChange < 0) {
        insights.push({
          id: 1,
          icon: "fa-solid fa-arrow-trend-down",
          title: `Views decreased by ${Math.abs(this.viewsChange)}%`,
          description: "Try improving your listing text and images to get more views",
          type: "warning",
          action: "Learn more"
        });
      }
      
      // Insight 2: Top performer
      if (this.mostViewedListing) {
        insights.push({
          id: 2,
          icon: "fa-solid fa-crown",
          title: "Top performer identified",
          description: `"${this.truncateText(this.mostViewedListing.title, 30)}" has ${this.mostViewedListing.views} views`,
          type: "info"
        });
      }
      
      // Insight 3: Category performance
      const categories = [...new Set(this.listings.map(l => l.category))];
      const categoryPerformance = categories.map(category => {
        const categoryListings = this.listings.filter(l => l.category === category);
        const avgViews = categoryListings.reduce((sum, l) => sum + (l.views || 0), 0) / categoryListings.length;
        return { category, avgViews, count: categoryListings.length };
      }).sort((a, b) => b.avgViews - a.avgViews);
      
      if (categoryPerformance.length > 1) {
        const bestCategory = categoryPerformance[0];
        insights.push({
          id: 3,
          icon: "fa-solid fa-chart-pie",
          title: `${bestCategory.category} performs best`,
          description: `Average ${bestCategory.avgViews.toFixed(1)} views per listing in this category`,
          type: "info"
        });
      }
      
      // Insight 4: Price range analysis
      const priceGroups = {
        low: this.listings.filter(l => l.price < 100).length,
        medium: this.listings.filter(l => l.price >= 100 && l.price < 500).length,
        high: this.listings.filter(l => l.price >= 500).length
      };
      
      const highestPriceGroup = Object.entries(priceGroups)
        .sort(([,a], [,b]) => b - a)[0];
      
      if (highestPriceGroup[1] > 0) {
        insights.push({
          id: 4,
          icon: "fa-solid fa-tag",
          title: "Price distribution insight",
          description: `Most listings (${highestPriceGroup[1]}) are in the ${highestPriceGroup[0]} price range`,
          type: "info"
        });
      }
      
      this.insights = insights;
    },
    
    // Filtering & Sorting
    filterListings() {
      let filtered = [...this.listings];
      
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(listing => 
          listing.title.toLowerCase().includes(query) ||
          listing.category.toLowerCase().includes(query) ||
          (listing.brand && listing.brand.toLowerCase().includes(query))
        );
      }
      
      this.filteredListings = filtered;
      this.currentPage = 1;
    },
    
    sortListings() {
      switch (this.sortBy) {
        case 'views_desc':
          this.filteredListings.sort((a, b) => (b.views || 0) - (a.views || 0));
          break;
        case 'views_asc':
          this.filteredListings.sort((a, b) => (a.views || 0) - (b.views || 0));
          break;
        case 'price_desc':
          this.filteredListings.sort((a, b) => b.price - a.price);
          break;
        case 'price_asc':
          this.filteredListings.sort((a, b) => a.price - b.price);
          break;
        case 'date_desc':
          this.filteredListings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
        case 'date_asc':
          this.filteredListings.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          break;
      }
    },
    
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filterListings();
      }, 300);
    },
    
    // Chart Methods
    // Builds the pie chart using top listings by views.
    initializeChart() {
      if (this.viewsChart) {
        this.viewsChart.destroy();
      }
      
      const ctx = this.$refs.viewsChart;
      if (!ctx || this.listings.length === 0) return;
      
      // Sort listings by views for better visualization
      const sortedListings = [...this.listings]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 10); // Top listings for readability in pie charts
      
      const labels = sortedListings.map((listing, index) => 
        `#${index + 1} - ${this.truncateText(listing.title, 15)}`
      );
      const data = sortedListings.map(listing => listing.views || 0);

      // Resolve App.vue CSS variables to concrete rgb strings (Chart.js draws on canvas).
      const resolveCssVar = (varName) => {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
        const tmp = document.createElement("div");
        tmp.style.color = raw;
        tmp.style.position = "absolute";
        tmp.style.left = "-9999px";
        document.body.appendChild(tmp);
        const resolved = getComputedStyle(tmp).color;
        tmp.remove();
        return resolved;
      };

      const borderColorResolved = resolveCssVar("--border-color-hover");
      const listingLabelColor = resolveCssVar("--text-color1");
      const pendingColor = resolveCssVar("--pending-color");
      const pendingBorder = resolveCssVar("--pending-border");
      const rejectedColor = resolveCssVar("--rejected-color");
      const rejectedBorder = resolveCssVar("--rejected-border");

      const parseRgb = (color) => {
        const match = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (!match) return { r: 0, g: 0, b: 0 };
        return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
      };

      const toRgba = (rgb, alpha) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;

      // Use App.vue semantic colors for better consistency.
      const piePalette = [
        pendingColor,
        pendingBorder,
        rejectedColor,
        rejectedBorder,
        borderColorResolved
      ];

      const toOpaque = (color) => {
        const rgb = parseRgb(color);
        return toRgba(rgb, 1);
      };

      const backgroundColors = data.map((_, i) => piePalette[i % piePalette.length]);
      const borderColors = data.map((_, i) => toOpaque(piePalette[i % piePalette.length]));

      const chartConfig = {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            label: 'Views',
            data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                color: listingLabelColor,
                generateLabels: (chart) => {
                  const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                  return labels.map((item, index) => {
                    if (index === 0) {
                      return {
                        ...item,
                        color: "purple",
                        fontColor: "purple"
                      };
                    }
                    return item;
                  });
                }
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value =
                    typeof context.raw === "number"
                      ? context.raw
                      : (typeof context.parsed === "number" ? context.parsed : context.parsed?.value) || 0;
                  const total = this.totalViews || 0;
                  const percent = total > 0 ? (value / total * 100).toFixed(1) : '0.0';
                  return `${context.label}: ${value} views (${percent}%)`;
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            intersect: true
          }
        }
      };
      
      this.viewsChart = new Chart(ctx, chartConfig);
    },
    
    // UI Helper Methods
    viewListingDetails(listing) {
      this.selectedListing = listing;
      this.showDetailsModal = true;
    },
    
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedListing = null;
    },
    
    // Shares the public buyer route for a listing.
    shareListing(listing) {
      const shareUrl = `${window.location.origin}/ipa/thirdparty/product/${listing.listing_id}`;
      if (navigator.share) {
        navigator.share({
          title: listing.title,
          text: `Check out this listing: ${listing.title}`,
          url: shareUrl
        });
      } else {
        navigator.clipboard.writeText(shareUrl);
        this.showToast('success', 'Copied', 'Listing link copied to clipboard');
      }
    },
    
    // Utility Methods
    // Sort and format helpers used by table/cards/insights.
    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toString();
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      } catch (error) {
        return 'Unknown date';
      }
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    getChangeClass(change) {
      if (change > 0) return 'positive';
      if (change < 0) return 'negative';
      return 'neutral';
    },
    
    getTrendIcon(change) {
      if (change > 0) return 'fa-solid fa-arrow-up';
      if (change < 0) return 'fa-solid fa-arrow-down';
      return 'fa-solid fa-minus';
    },
    
    getRankClass(index) {
      if (index === 0) return 'rank-1';
      if (index === 1) return 'rank-2';
      if (index === 2) return 'rank-3';
      return 'rank-other';
    },
    
    getStatusIcon(listing) {
      if (listing.is_sold) return 'fa-solid fa-check-circle';
      if (listing.is_active) return 'fa-solid fa-circle-play';
      return 'fa-solid fa-circle-pause';
    },
    
    getInsightClass(insight) {
      return `insight-${insight.type}`;
    },
    
    calculateViewPercentage(views) {
      if (this.totalViews === 0) return 0;
      return (views / this.totalViews) * 100;
    },
    
    calculateDailyViews(listing) {
      const daysActive = Math.max(1, Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24)));
      return Math.round((listing.views || 0) / daysActive);
    },
    
    calculateEngagementScore(listing) {
      const views = listing.views || 0;
      const price = listing.price || 0;
      const daysActive = Math.max(1, Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24)));
      
      const viewScore = Math.min(views / daysActive, 100);
      const priceScore = price > 1000 ? 80 : price > 500 ? 90 : 100;
      
      return Math.round((viewScore * 0.7 + priceScore * 0.3) * 10) / 10;
    },
    
    getOptimizationSuggestions(listing) {
      const suggestions = [];
      
      if ((listing.images?.length || 0) < 3) {
        suggestions.push({
          id: 1,
          icon: 'fa-solid fa-image',
          text: 'Add more images (3+ recommended)',
          type: 'image'
        });
      }
      
      if (!listing.brand) {
        suggestions.push({
          id: 2,
          icon: 'fa-solid fa-tag',
          text: 'Add brand name for better visibility',
          type: 'brand'
        });
      }
      
      if (listing.views < 10 && listing.created_at) {
        const daysActive = Math.max(1, Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24)));
        if (daysActive > 7) {
          suggestions.push({
            id: 3,
            icon: 'fa-solid fa-hashtag',
            text: 'Update keywords and description to improve visibility',
            type: 'keywords'
          });
        }
      }
      
      // Price suggestion based on category average
      if (listing.category) {
        const categoryListings = this.listings.filter(l => l.category === listing.category && l.listing_id !== listing.listing_id);
        if (categoryListings.length > 0) {
          const avgPrice = categoryListings.reduce((sum, l) => sum + l.price, 0) / categoryListings.length;
          const priceDiff = ((listing.price - avgPrice) / avgPrice) * 100;
          
          if (priceDiff > 20) {
            suggestions.push({
              id: 4,
              icon: 'fa-solid fa-money-bill-wave',
              text: `Price is ${priceDiff.toFixed(1)}% above category average`,
              type: 'price'
            });
          } else if (priceDiff < -20) {
            suggestions.push({
              id: 5,
              icon: 'fa-solid fa-money-bill-wave',
              text: `Price is ${Math.abs(priceDiff).toFixed(1)}% below category average`,
              type: 'price'
            });
          }
        }
      }
      
      return suggestions;
    },
    
    handleInsightAction(insight) {
      if (insight.id === 1 && insight.type === 'warning') {
        // View optimization action
        this.showToast('info', 'Optimization Tips', 'Consider improving listing titles and images');
      }
    },
    
    implementSuggestion(suggestion) {
      if (!this.selectedListing) return;
      
      if (suggestion.type === 'image') {
        this.$router.push(`/seller/edit-listing/${this.selectedListing.listing_id}?tab=images`);
      } else if (suggestion.type === 'brand') {
        this.$router.push(`/seller/edit-listing/${this.selectedListing.listing_id}?tab=details`);
      } else if (suggestion.type === 'keywords') {
        this.$router.push(`/seller/edit-listing/${this.selectedListing.listing_id}?tab=seo`);
      } else if (suggestion.type === 'price') {
        this.$router.push(`/seller/edit-listing/${this.selectedListing.listing_id}?tab=pricing`);
      }
      this.closeDetailsModal();
    },
    
    // Pagination Methods
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    goToPage(page) {
      this.currentPage = page;
    },
    
    resetPagination() {
      this.currentPage = 1;
    },
    
    // Toast Methods
    showToast() {
      // Stub: wire to app toast if this view should surface notifications
    }
  },
  
  watch: {
    listings() {
      // Regenerate insights when listings change
      this.generateInsights();
    }
  }
};
</script>

<style scoped>
/* Import your CSS variables */
.listing-views-container {
  min-height: 100vh;
  background: var(--background-color1);
  color: var(--text-color1);
  font-family: 'Poppins', sans-serif;
  padding-top: 0;
}

/* Ensure consistent styling */
.views-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  padding: 80px 0;
}

.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

/* Header Styles */
.views-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
}

.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-content .subtitle {
  color: var(--text-color2);
  font-size: 14px;
}

/* Time Range Selector */
.time-range-selector {
  display: flex;
  gap: 5px;
  background: var(--background-color2);
  padding: 5px;
  border-radius: 10px;
  box-shadow: 0 2px 10px var(--box-shadow);
}

.time-range-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: var(--text-color1);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 14px;
}

.time-range-btn.active {
  background: var(--primary-color);
  color: white;
}

.time-range-btn:hover:not(.active) {
  background: var(--background-color3);
}

/* Overview Stats */
.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: var(--background-color2);
  border-radius: 15px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 4px 15px var(--box-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 20px var(--box-shadow);
}

.stat-icon {
  width: 50px;
  height: 50px;
  background: var(--gradient);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.stat-content h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 5px;
  color: var(--text-color1);
}

.stat-content p {
  color: var(--text-color2);
  font-size: 14px;
  margin-bottom: 8px;
}

.stat-change {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.stat-change.positive {
  color: #4CAF50;
}

.stat-change.negative {
  color: #f44336;
}

.stat-change.neutral {
  color: var(--text-color2);
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: auto auto;
  gap: 25px;
  grid-template-areas:
    "chart performers"
    "table insights";
  min-width: 0;
}

.chart-container {
  grid-area: chart;
  background: var(--background-color2);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px var(--box-shadow);
  min-width: 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.chart-header h3 {
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color1);
}

.chart-type-select {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

.chart-wrapper {
  height: clamp(220px, 35vh, 340px);
  position: relative;
  width: 100%;
}

.chart-wrapper canvas {
  width: 100% !important;
  height: 100% !important;
}

/* Top Performers */
.top-performers {
  grid-area: performers;
  background: var(--background-color2);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px var(--box-shadow);
  min-width: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-refresh-small {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh-small:hover {
  background: var(--primary-color);
  color: white;
}

.fa-spin {
  animation: fa-spin 1s linear infinite;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.performers-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.performer-item {
  background: var(--background-color1);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.performer-item:hover {
  border-color: var(--border-color-hover);
  transform: translateX(5px);
}

.performer-rank {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.rank-badge {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  color: white;
}

.rank-1 {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
}

.rank-2 {
  background: linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%);
}

.rank-3 {
  background: linear-gradient(135deg, #CD7F32 0%, #8B4513 100%);
}

.rank-other {
  background: var(--background-color3);
  color: var(--text-color1);
}

.listing-image {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--background-color2);
}

.listing-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.performer-details {
  flex: 1;
}

.listing-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-color1);
}

.listing-stats {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.listing-stats .stat {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-color2);
}

.listing-stats .stat i {
  font-size: 10px;
}

.view-progress {
  margin-top: 10px;
}

.progress-bar {
  height: 6px;
  background: var(--background-color3);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 11px;
  color: var(--text-color2);
  display: block;
  text-align: right;
}

/* Listings Table */
.listings-table-container {
  grid-area: table;
  background: var(--background-color2);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px var(--box-shadow);
  min-width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.table-header h3 {
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-controls {
  display: flex;
  gap: 15px;
  align-items: center;
}

.search-box {
  position: relative;
  background: var(--background-color1);
  border-radius: 25px;
  padding: 8px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 250px;
}

.search-box i {
  color: var(--text-color2);
  font-size: 14px;
}

.search-box input {
  background: transparent;
  border: none;
  color: var(--text-color1);
  flex: 1;
  font-size: 14px;
  outline: none;
}

.filter-select {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  min-width: 150px;
}

.table-responsive {
  overflow-x: auto;
}

.listings-table {
  width: 100%;
  border-collapse: collapse;
}

.listings-table thead {
  background: var(--background-color1);
}

.listings-table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: var(--text-color2);
  font-size: 14px;
  border-bottom: 2px solid var(--background-color3);
}

.listings-table tbody tr {
  border-bottom: 1px solid var(--background-color3);
  transition: all 0.3s ease;
  cursor: pointer;
}

.listings-table tbody tr:hover {
  background: var(--background-color1);
}

.listing-row td {
  padding: 15px;
  vertical-align: middle;
}

.listing-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.listing-image-small {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--background-color3);
  flex-shrink: 0;
}

.listing-image-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color2);
  font-size: 20px;
}

.listing-text {
  display: flex;
  flex-direction: column;
}

.listing-text strong {
  font-size: 14px;
  margin-bottom: 3px;
}

.listing-text small {
  font-size: 12px;
  color: var(--text-color2);
}

.category-cell .category-badge {
  background: var(--role-item);
  color: var(--text-color1);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.price-cell {
  font-size: 16px;
  font-weight: 600;
}

.price-cell .negotiable-badge {
  display: block;
  font-size: 11px;
  color: var(--primary-color);
  font-weight: 500;
  margin-top: 3px;
}

.views-cell .views-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.views-cell .views-display i {
  color: var(--primary-color);
  font-size: 12px;
}

.views-trend {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.views-trend.trend-up {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.views-trend.trend-down {
  background: rgba(244, 67, 54, 0.2);
  color: #f44336;
}

.status-cell .status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.status-sold {
  background: rgba(255, 152, 0, 0.2);
  color: #FF9800;
}

.status-inactive {
  background: rgba(158, 158, 158, 0.2);
  color: #9E9E9E;
}

.date-cell {
  font-size: 13px;
  color: var(--text-color2);
}

.actions-cell .action-buttons {
  display: flex;
  gap: 8px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
}

.btn-view {
  background: rgba(0, 215, 255, 0.1);
  color: #00D7FF;
}

.btn-view:hover {
  background: #00D7FF;
  color: white;
}

.btn-share {
  background: rgba(156, 39, 176, 0.1);
  color: #9C27B0;
}

.btn-share:hover {
  background: #9C27B0;
  color: white;
}

/* Pagination */
.pagination-controls {
  margin-top: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  padding-top: 20px;
  border-top: 1px solid var(--background-color3);
}

.pagination-info {
  color: var(--text-color2);
  font-size: 14px;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-btn {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.page-numbers {
  display: flex;
  gap: 5px;
}

.page-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.page-btn:hover:not(.active) {
  background: var(--background-color3);
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color2);
  font-size: 14px;
}

.page-size-select {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

/* Insights Container */
.insights-container {
  grid-area: insights;
  background: var(--background-color2);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px var(--box-shadow);
}

.insights-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.insight-card {
  background: var(--background-color1);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 15px;
  border-left: 4px solid transparent;
}

.insight-positive {
  border-left-color: #4CAF50;
}

.insight-info {
  border-left-color: #2196F3;
}

.insight-warning {
  border-left-color: #FF9800;
}

.insight-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.insight-positive .insight-icon {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.insight-info .insight-icon {
  background: rgba(33, 150, 243, 0.2);
  color: #2196F3;
}

.insight-warning .insight-icon {
  background: rgba(255, 152, 0, 0.2);
  color: #FF9800;
}

.insight-content {
  flex: 1;
}

.insight-content h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  color: var(--text-color1);
}

.insight-content p {
  font-size: 12px;
  color: var(--text-color2);
  margin-bottom: 10px;
  line-height: 1.4;
}

.btn-insight {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-insight:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--background-color2);
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 25px 30px;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-close {
  background: var(--background-color1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color1);
  font-size: 18px;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: var(--primary-color);
  color: white;
}

.modal-body {
  padding: 30px;
}

.listing-details {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.details-header {
  margin-bottom: 20px;
}

.listing-title-section h4 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 15px;
  color: var(--text-color1);
}

.listing-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
}

.listing-meta span {
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.listing-meta .category {
  background: rgba(0, 215, 255, 0.1);
  color: #00D7FF;
}

.listing-meta .price {
  background: rgba(255, 193, 7, 0.1);
  color: #FFC107;
  font-weight: 600;
}

.listing-meta .views {
  background: rgba(76, 175, 80, 0.1);
  color: #4CAF50;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
}

.detail-card {
  background: var(--background-color1);
  border-radius: 15px;
  padding: 20px;
}

.detail-card h5 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.metric {
  text-align: center;
}

.metric-label {
  display: block;
  font-size: 12px;
  color: var(--text-color2);
  margin-bottom: 5px;
}

.metric-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-color1);
}

.mini-chart {
  height: 150px;
  position: relative;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.suggestion-item {
  background: var(--background-color2);
  padding: 15px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.suggestion-item:hover {
  transform: translateX(5px);
  background: var(--background-color3);
}

.suggestion-item i {
  color: var(--primary-color);
  font-size: 16px;
  width: 24px;
}

.suggestion-item span {
  flex: 1;
  font-size: 14px;
  color: var(--text-color1);
}

.btn-implement {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 6px 15px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-implement:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      "chart"
      "performers"
      "table"
      "insights";
  }
}

@media (max-width: 768px) {
  .views-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .time-range-selector {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .overview-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .table-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    min-width: unset;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .pagination-buttons {
    justify-content: center;
  }
  
  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .overview-stats {
    grid-template-columns: 1fr;
  }

  .chart-type-select {
    width: 100%;
  }

  .chart-wrapper {
    height: clamp(200px, 30vh, 280px);
  }
  
  .stat-card {
    flex-direction: column;
    text-align: center;
  }
  
  .listing-info {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .listing-stats {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* Scrollbar Styling */
.modal-content::-webkit-scrollbar,
.views-content::-webkit-scrollbar {
  width: 8px;
}

.modal-content::-webkit-scrollbar-track,
.views-content::-webkit-scrollbar-track {
  background: var(--background-color1);
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb,
.views-content::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb:hover,
.views-content::-webkit-scrollbar-thumb:hover {
  background: var(--border-color-hover3);
}

/* Selection */
::selection {
  background: var(--primary-color);
  color: white;
}

/* Focus States */
.time-range-btn:focus,
.chart-type-select:focus,
.filter-select:focus,
.search-box input:focus,
.btn-action:focus,
.btn-insight:focus,
.btn-implement:focus {
  outline: 2px solid var(--border-color-hover3);
  outline-offset: 2px;
}

/* Loading States */
.loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 50px 20px;
  color: var(--text-color2);
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h4 {
  font-size: 18px;
  margin-bottom: 10px;
  color: var(--text-color1);
}
</style>